<template lang="pug">
el-main(v-loading="loading")
  h2(v-if="id") {{form.code | uppercase}}
  h2(v-else) New Store

  el-form(ref="form" :model="form" :rules="rules" label-width="120px" :disabled="formIsDisabled")
    el-form-item.text-right
      el-button(type="primary" @click="submit" :disabled="formIsDisabled") Save
      el-button(
        v-if="storeId"
        type="danger"
        @click="deleteAirportStore"
        :disabled="formIsDisabled") Delete
    el-form-item(label="Name" prop="name")
      el-input(v-model="form.name" placeholder="Name")
    el-form-item(label="Description" prop="description")
      el-input(type="textarea" v-model="form.description" placeholder="Description")
    el-form-item(label="Section" prop="section")
      el-select(v-model="form.section" placeholder="Select Section")
        el-option(v-for="item in sectionOptions"
          :key="item.name"
          :label="item.name"
          :value="item.id")
    el-form-item(label="Type" prop="type")
      el-cascader(placeholder="Try searching: Shopping"
        :options="typeOptions"
        filterable
        v-model="form.type"
        change-on-select
        clearable)
    el-form-item(label="Location" prop="location")
      el-input(v-model="form.location" placeholder="i.e. closest gate")
    el-form-item(label="Open At" prop="openAt")
      el-select(v-model="form.openAt" placeholder="Open Time")
        el-option(v-for="item in startHourOptions"
          :key="item"
          :label="item | toAmPm"
          :value="item")
    transition(name="el-fade-in-linear")
      el-form-item(label="Close At" prop="closeAt" v-if="form.openAt !== '24 hours'")
        el-select(v-model="form.closeAt" placeholder="Close Time")
          el-option(v-for="item in endHourOptions"
            :key="item"
            :label="item | toAmPm"
            :value="item")
    el-form-item(label="Weekend Times" prop="weekendTimes.active")
      el-switch(v-model="form.weekendTimes.active")
    el-form-item(label="Custom Days" prop="customWeekDays.active" v-if="form.weekendTimes.active")
      el-switch(v-model="form.customWeekDays.active")
      el-form-item(label="Custom Days" v-if="form.customWeekDays.active")
        el-input(v-model="form.customDays" placeholder="Custom workdays")
    transition(name="el-fade-in-linear")
      el-form-item(label="Open At" prop="openAt" v-if="form.weekendTimes.active")
        el-select(v-model="form.weekendTimes.openAt" placeholder="Open Time")
          el-option(v-for="item in startHourOptions"
            :key="item"
            :label="item | toAmPm"
            :value="item")
    transition(name="el-fade-in-linear")
      el-form-item(label="Close At" prop="closeAt"
        v-if="form.weekendTimes.openAt !== '24 hours' && form.weekendTimes.active")
        el-select(v-model="form.weekendTimes.closeAt" placeholder="Close Time")
          el-option(v-for="item in endHourOptions"
            :key="item"
            :label="item | toAmPm"
            :value="item")
    ImageUpload(
      label="Image"
      :uploadURL="'/s3/signed-upload-url'"
      v-model="form.imageUrl"
      )
    el-form-item(label="Special Offer" prop="specialOffer.active")
      el-switch(v-model="form.specialOffer.active")
    transition(name="el-fade-in-linear")
      el-form-item(label="Title Text"
        v-if="form.specialOffer.active"
        prop="specialOffer.title")
        el-input(v-model="form.specialOffer.title")
    transition(name="el-fade-in-linear")
      el-form-item(label="Subtitle Text"
        v-if="form.specialOffer.active"
        prop="specialOffer.subtitle")
        el-input(v-model="form.specialOffer.subtitle")
    transition(name="el-fade-in-linear")
      el-form-item(label="Dates"
        v-if="form.specialOffer.active"
        prop="specialOffer.dates")
        el-date-picker(v-model="form.specialOffer.dates"
          type="daterange"
          range-separator="To"
          start-placeholder="Start date"
          end-placeholder="End date"
          format="yyyy/MM/dd"
          value-format="yyyy-MM-dd"
          )
    transition(name="el-fade-in-linear")
      ImageUpload(
        v-if="form.specialOffer.active"
        label="Background"
        :uploadURL="'/s3/signed-upload-url'"
        v-model="form.specialOffer.background"
      )
</template>

<script>
import AirportsService from '@/services/AirportsService';
import ImageUpload from '@/components/ImageUpload';

const startHours = [];

for (let hour = 0; hour < 24; hour += 1) {
  for (let minute = 0; minute < 60; minute += 15) {
    startHours.push(`${`0${hour}`.substr(-2)}:${`0${minute}`.substr(-2)}`);
  }
}

startHours.unshift('24 hours');

const endHours = [];

for (let hour = 0; hour < 24; hour += 1) {
  for (let minute = 0; minute < 60; minute += 15) {
    endHours.push(`${`0${hour}`.substr(-2)}:${`0${minute}`.substr(-2)}`);
  }
}

endHours.push('Last flight');
endHours.push('30 min after last flight');

export default {
  components: { ImageUpload },
  filters: {
    uppercase(string) {
      if (!string) {
        return '';
      }
      return string.toUpperCase();
    },
    toAmPm(time) {
      const [hour, minute] = time.split(':');
      if (!hour || !minute) {
        return time;
      }
      const hourInt = parseInt(hour, 10);
      const minuteInt = parseInt(minute, 10);

      if (hourInt === 0 && minuteInt === 0) {
        return '12:00 midnight';
      }

      let postfix = 'am';
      if (hourInt >= 12) {
        postfix = 'pm';
      }

      let hourText = hourInt % 12;
      if (hourText === 0) {
        hourText = 12;
      }

      return `${hourText}:${minute} ${postfix}`;
    },
  },
  props: {
    id: {
      default: null,
      type: String,
    },
    storeId: {
      default: null,
      type: String,
    },
  },
  data() {
    return {
      startHourOptions: startHours,
      endHourOptions: endHours,
      formIsDisabled: false,
      form: {
        name: '',
        description: '',
        section: '',
        type: [],
        imageUrl: '',
        specialOffer: {
          active: false,
          title: '',
          subtitle: '',
          dates: null,
          background: '',
        },
        weekendTimes: {
          active: false,
        },
        customWeekDays: {
          active: false,
        },
        openAt: null,
        closeAt: null,
      },
      sectionOptions: [],
      typeOptions: [
        {
          value: 'Shopping',
          label: 'Shopping',
          children: [
            {
              value: 'Retail',
              label: 'Retail',
            },
            {
              value: 'News/Gift',
              label: 'News/Gift',
            },
            {
              value: 'Duty Free',
              label: 'Duty Free',
            },
            {
              value: 'Other',
              label: 'Other',
            },
          ],
        },
        {
          value: 'Dining',
          label: 'Dining',
          children: [
            {
              value: 'Sit-down',
              label: 'Sit-down',
            },
            {
              value: 'Quick Serve',
              label: 'Quick Serve',
            },
            {
              value: 'Other',
              label: 'Other',
            },
          ],
        },
        {
          value: 'Services',
          label: 'Services',
          children: [
            {
              value: 'Currency Exchange',
              label: 'Currency Exchange',
            },
            {
              value: 'Shoeshine',
              label: 'Shoeshine',
            },
            {
              value: 'Other',
              label: 'Other',
            },
          ],
        },
      ],
      rules: {
        name: [
          {
            required: true,
            message: 'Please input Name',
            trigger: 'blur',
          },
        ],
        description: [
          {
            required: true,
            message: 'Please input Description',
            trigger: 'blur',
          },
        ],
        type: [
          {
            required: true,
            message: 'Please select Type',
            trigger: 'change',
          },
        ],
        section: [
          {
            required: true,
            message: 'Please select Section',
            trigger: 'change',
          },
        ],
        location: [
          {
            required: true,
            message: 'Please input Location',
            trigger: 'blur',
          },
        ],
        openAt: [
          {
            required: true,
            message: 'Please input Open Hours',
            trigger: 'blur',
          },
        ],
        closeAt: [
          {
            required: true,
            message: 'Please input Close Hours',
            trigger: 'blur',
          },
        ],
        'specialOffer.title': [
          {
            required: true,
            message: 'Please input Title',
            trigger: 'blur',
          },
        ],
      },
      homeFileList: [],
      promoFileList: [],
      loading: false,
    };
  },
  watch: {
    $route() {
      this.fetchData();
    },
  },
  created() {
    this.fetchData();
  },
  methods: {
    submit() {
      this.$refs.form.validate(valid => {
        if (valid) {
          this.formIsDisabled = true;
          this.loading = true;
          if (!this.storeId) {
            AirportsService.createAirportStore(this.id, this.form)
              .then(({ data }) => {
                this.$message({
                  type: 'success',
                  message: 'Submitted',
                });
                this.$router.push({
                  name: 'AdminAirportStore',
                  params: { id: this.id, storeId: data.id },
                });
              })
              .catch(error => {
                this.$message({
                  type: 'error',
                  message:
                    error.response.data.errorMessage || error.response.data,
                });
              })
              .finally(() => {
                this.formIsDisabled = false;
                this.loading = false;
              });
          } else {
            AirportsService.updateAirportStore(this.id, this.storeId, this.form)
              .then(() => {
                this.$message({
                  type: 'success',
                  message: 'Saved',
                });
              })
              .catch(error => {
                this.$message({
                  type: 'error',
                  message:
                    error.response.data.errorMessage || error.response.data,
                });
              })
              .finally(() => {
                this.formIsDisabled = false;
                this.loading = false;
              });
          }
        } else {
          this.$message({
            type: 'error',
            message: 'Missing required fields',
          });
        }
      });
    },
    resetForm() {
      this.$refs.form.resetFields();
    },
    fetchData() {
      AirportsService.getAirportSections(this.id)
        .then(({ data }) => {
          this.sectionOptions = data.sections.concat();
        })
        .catch(error => {
          this.$message({
            type: 'error',
            message: error.response
              ? error.response.data.errorMessage
              : error.message,
          });
        });

      this.formIsDisabled = false;
      this.loading = false;
      if (!this.storeId) {
        this.form.name = '';
        this.form.description = '';
        this.form.section = '';
        this.form.type = [];
        this.form.imageUrl = '';
        this.form.specialOffer.active = false;
        this.form.specialOffer.title = '';
        this.form.specialOffer.subtitle = '';
        this.form.specialOffer.dates = null;
        this.form.weekendTimes.active = false;
        this.form.customWeekDays.active = false;
        this.form.openAt = null;
        this.form.closeAt = null;
        setTimeout(() => {
          this.resetForm();
        }, 0);

        return;
      }

      this.loading = true;
      AirportsService.getAirportStore(this.id, this.storeId)
        .then(({ data }) => {
          this.form = Object.assign({}, this.form, data);
        })
        .catch(error => {
          this.$message({
            type: 'error',
            message: error.response
              ? error.response.data.errorMessage
              : error.message,
          });
          setTimeout(() => {
            this.$router.push({ name: 'NewAdminAirportStore' });
          }, 1000);
        })
        .finally(() => {
          this.loading = false;
        });
    },
    deleteAirportStore() {
      this.$confirm('Are you sure you want to delete this store?', 'Warning', {
        type: 'warning',
        confirmButtonText: 'Yes, Delete!',
      })
        .then(() => {
          AirportsService.deleteAirportStore(this.id, this.storeId)
            .then(() => {
              this.fetchData();
            })
            .catch(error => {
              this.$message({
                type: 'error',
                message:
                  error.response.data.errorMessage || error.response.data,
              });
            });
        })
        .catch(err => {
          this.$message({
            type: 'info',
            message: err,
          });
        });
    },
    handleImageCompleted(file) {
      [this.form.imageUrl] = file.uploadURL.split('?');
    },
    handleImageRemove() {
      this.form.imageUrl = null;
    },
    handleSpecialOfferImageCompleted(file) {
      [this.form.specialOffer.background] = file.uploadURL.split('?');
    },
    handleSpecialOfferImageRemove() {
      this.form.specialOffer.background = null;
    },
    handleWeekTimeChange(value) {
      this.form.weekTimes = Object.assign({}, this.form.weekTimes, value);
    },
  },
};
</script>

<style lang="stylus" scoped>
.el-input-group__append
  .el-button--danger
    color red

.el-cascader, .el-select, .el-date-editor
  width 100%
</style>




// WEBPACK FOOTER //
// src/components/AdminAirportStore.vue